import React, { useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { api } from "@/redux/api";
import message from "@/utils/globalMessage";
import { formatContractRef } from "@/utils/entity-ref";
import { useAuthEditable } from "@/hooks/use-auth";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import {
  WORKORDER_CREATE_PREFILL_KEY,
  writeFlowPrefill,
} from "@/utils/flow-prefill";
import ContractDetail from "@/containers/contract/ContractDetail";

const toAmountNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

export default function FacilityContractDetailPage() {
  const { companyId, facilityId, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const facilityContext = useSelector((state) => getFacilityContext(state));
  // contract:change gates both note writing and editing the CC email list.
  const canEditContract = useAuthEditable("contract");
  const canAddNote = canEditContract;
  const effectiveFacilityContext = {
    companyId: companyId || facilityContext.companyId,
    facilityId: facilityId || facilityContext.facilityId,
  };
  const baseListPath =
    buildFacilityPath("contract-request", effectiveFacilityContext) || "/switch-view";
  const listPath = `${baseListPath}${location.search || ""}`;

  const loadRequest = useCallback(
    (requestId) => api.getContract(requestId),
    []
  );
  const addNote = useCallback(
    (requestId, body) => api.addContractNote(requestId, body),
    []
  );
  const deleteNote = useCallback(
    (requestId, noteId) => api.deleteContractNote(requestId, noteId),
    []
  );
  const deleteNoteAttachment = useCallback(
    (requestId, noteId, attachmentId) =>
      api.deleteContractNoteAttachment(requestId, noteId, attachmentId),
    []
  );
  const resolveContext = useCallback(
    (record) => ({
      companyId: record?.company_id || effectiveFacilityContext.companyId,
      facilityId:
        record?.facility || record?.facility_id || effectiveFacilityContext.facilityId,
    }),
    [effectiveFacilityContext.companyId, effectiveFacilityContext.facilityId]
  );
  const getWorkorderPath = useCallback(
    (record) => {
      const recordContext = {
        ...resolveContext(record),
        facilityId:
          record?.workorder_facility_id || record?.facility || effectiveFacilityContext.facilityId,
      };
      return (
        buildFacilityPath(`workorder-detail/${record.workorder_id}`, recordContext) ||
        "/switch-view"
      );
    },
    [
      effectiveFacilityContext.companyId,
      effectiveFacilityContext.facilityId,
      resolveContext,
    ]
  );
  const getTenantRequestPath = useCallback(
    (record) => {
      const recordContext = {
        ...resolveContext(record),
        facilityId:
          record?.service_request_facility_id ||
          record?.facility ||
          effectiveFacilityContext.facilityId,
      };
      return (
        buildFacilityPath(
          `service-request-detail/${record.service_request_id}`,
          recordContext
        ) || "/switch-view"
      );
    },
    [
      effectiveFacilityContext.companyId,
      effectiveFacilityContext.facilityId,
      resolveContext,
    ]
  );
  const createWorkorder = useCallback(
    (record) => {
      const recordContext = {
        companyId: record?.company_id || effectiveFacilityContext.companyId,
        facilityId: record?.facility || effectiveFacilityContext.facilityId,
      };
      const createPath = buildFacilityPath("create-workorder", recordContext);
      if (!createPath || !record) return;
      const serviceRequestId = record.service_request_id || null;
      const saved = writeFlowPrefill(WORKORDER_CREATE_PREFILL_KEY, {
        source: "contract-request",
        source_id: record.id,
        sourceLabel: `Contract Request ${formatContractRef(record.id)}`,
        contract_request: record.id,
        service_request: serviceRequestId,
        service_request_id: serviceRequestId,
        facility: record.facility || recordContext.facilityId,
        equipments: record.equipments || [],
        service_category: record.service_category || null,
        issue: record.title || "",
        description: record.description || "",
        priority: record.priority || null,
        due_date: record.due_date || null,
        estimate_budget: toAmountNumber(record.awarded_amount),
        contractor: record.assignTo || null,
      });
      if (!saved) {
        message.error("Unable to prepare the work order form. Please try again.");
        return;
      }
      navigate(createPath);
    },
    [effectiveFacilityContext.companyId, effectiveFacilityContext.facilityId, navigate]
  );

  return (
    <ContractDetail
      role="facility"
      requestId={id}
      listPath={listPath}
      loadRequest={loadRequest}
      addNote={addNote}
      deleteNote={deleteNote}
      deleteNoteAttachment={deleteNoteAttachment}
      canAddNote={canAddNote}
      updateCcEmails={api.updateContractCcEmails}
      canEditCcEmails={canEditContract}
      closeContract={api.closeContract}
      cancel={api.cancelContract}
      reactivate={api.reactivateContract}
      award={api.awardContract}
      createWorkorder={createWorkorder}
      getWorkorderPath={getWorkorderPath}
      getTenantRequestPath={getTenantRequestPath}
    />
  );
}
